import { CompiledQuery } from 'kysely'
import { useEffect, useRef, useState } from 'react'
import {
  useComparatorRef,
  useStateTools,
} from '@pogokid/react-hooks'
import { captureException, getAllLogger } from '@pogokid/log'
import {
  debounce,
  getErrorMessage,
  isDeepEqual,
} from '@pogokid/util'
import { AsyncQueryResponse } from '@soniq/public-resource'
import { getNotificationBus } from '../core/NotificationBus'
import { useDblite } from './context.ts'

const log = getAllLogger('ui:useReactiveQuery')

export type ReactiveQueryState = 'empty' | 'set'

export interface UseReactiveQueryResponse<Schema>
  extends AsyncQueryResponse<Schema> {
  queryState: ReactiveQueryState
}

export function useReactiveQuery<Schema>({
  query,
  enabled,
  normaliseRecordData = (data) => data,
  channel = 'domain_event',
}: {
  query: CompiledQuery
  enabled: boolean
  normaliseRecordData?: (data: Schema) => Schema
  channel?: string
}): UseReactiveQueryResponse<Schema> {
  const { db } = useDblite()
  const notificationBus = getNotificationBus()
  const [queryState, setQueryState] =
    useState<ReactiveQueryState>('empty')
  const [rows, getRows, setRows] = useStateTools<{
    rows: Schema[]
  }>({ rows: [] })
  const [error, setError] = useState<Error | null>(null)
  const mountedRef = useRef(true)
  const abortControllerRef = useRef<AbortController | null>(null)

  function runQuery(value: CompiledQuery) {
    // Cancel any previous in-flight query
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create a new AbortController for this query
    const controller = new AbortController()
    abortControllerRef.current = controller

    db.executeQuery<Schema>(value)
      .then((results) => {
        // Check if query was aborted or component unmounted
        if (controller.signal.aborted || !mountedRef.current) {
          return
        }

        const bothZero =
          getRows().rows.length === 0 &&
          results?.rows?.length === 0
        if (results.rows && !bothZero) {
          if (!isDeepEqual(getRows().rows, results.rows)) {
            log.verbose(
              'runQuery set rows',
              value.sql,
              getRows().rows,
              results.rows
            )
            setRows({
              rows: results.rows?.map(normaliseRecordData) ?? [],
            })
          }
        }
        setQueryState('set')
        setError(null)
      })
      .catch((err) => {
        // Ignore errors from aborted queries
        if (controller.signal.aborted || !mountedRef.current) {
          return
        }

        const error = new Error(
          `There was an error running query: ${getErrorMessage(
            err
          )}`,
          { cause: err }
        )
        captureException(error)
        setError(error)
      })
      .finally(() => {
        // Clear the reference if this is the current controller
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null
        }
      })
  }

  const reactiveQueryRef = useComparatorRef<CompiledQuery>(
    enabled ? query : null,
    (a, b) => {
      let equal = true
      if (a?.sql !== b?.sql) {
        return false
      }
      if (a?.parameters.length !== b?.parameters.length) {
        return false
      }
      if (Array.isArray(a?.parameters)) {
        equal = a.parameters.every(
          (v, i) => v === b?.parameters[i]
        )
      }
      return equal
    },
    (value) => {
      if (enabled && value) {
        runQuery(value)
      }
    },
    true
  )

  // Subscribe to change notifications via NotificationBus
  useEffect(() => {
    const changed = debounce((payload: string) => {
      if (payload === 'applied' && reactiveQueryRef.current) {
        try {
          runQuery(reactiveQueryRef.current)
        } catch (error) {
          log.error('Error running reactive query', error)
        }
      }
    }, 100)
    log.verbose('listen')
    notificationBus.listen(channel, changed)
    return () => {
      log.verbose('unlisten')
      changed.cancel()
      notificationBus.unlisten(channel, changed)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationBus, channel])

  // Cleanup on unmount: abort pending queries and mark component as unmounted
  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
    }
  }, [])

  return {
    value: rows.rows,
    queryState,
    loading: enabled ? queryState === 'empty' && !error : false,
    error,
  }
}
